/* ─────────────────────────────────────────────────────────────────────────
   Tykables 7.0 — Cart drawer enhancements
   Listens to Dawn's cartUpdate PubSub events to live-refresh:
     1. Free-shipping progress bar
     2. Promo code input / applied state
   Both elements are optional — if the merchant has the features disabled
   in theme settings, the corresponding DOM is absent and the handlers no-op.
   ───────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  // Safety: bail if Dawn's pubsub isn't on the page yet.
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS !== 'object') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    if (typeof subscribe !== 'function') return;
    setupProgressBar();
    setupPromoInput();
    setupBundleRemove();
    groupBundleLines();

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var cart = (event && event.cartData) ? event.cartData : null;
      if (!cart) {
        fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart.js' : '/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (data) { refresh(data); })
          .catch(function () {});
      } else {
        refresh(cart);
      }
    });

    // Re-run grouping when the cart DOM re-renders (Dawn replaces content on update)
    var cartObserverTarget = document.querySelector('#CartDrawer, #main-cart-items, .tyk-cart-table');
    if (cartObserverTarget && typeof MutationObserver === 'function') {
      var obs = new MutationObserver(function() { groupBundleLines(); });
      obs.observe(cartObserverTarget, { childList: true, subtree: true });
    }
  }

  function refresh(cart) {
    refreshProgressBar(cart);
    refreshPromoApplied(cart);
    refreshCheckoutUrl(cart);
    groupBundleLines();
  }

  /* ── Remove-bundle group action ───────────────────────────────────
     Wires up [data-tyk-cart-remove-bundle] buttons rendered server-side
     on the first row of each bundle group. Uses event delegation so the
     handler survives Dawn's drawer re-renders without re-binding.
     The button's data-bundle-keys attribute carries every line key in
     the bundle (pipe-separated, pre-collected in Liquid) — one
     /cart/update.js POST clears the whole group. */
  function setupBundleRemove() {
    if (document.__tykBundleRemoveBound) return;
    document.__tykBundleRemoveBound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[data-tyk-cart-remove-bundle]');
      if (!btn) return;
      e.preventDefault();
      var raw = btn.dataset.bundleKeys || '';
      var keys = raw.split('|').filter(function (k) { return k && k.length > 0; });
      if (keys.length === 0) return;
      // Confirm — clears multiple lines at once and the attached
      // bundle discount goes away with them.
      if (!window.confirm('Remove this bundle and all ' + keys.length + ' items?')) return;
      var updates = {};
      keys.forEach(function (k) { updates[k] = 0; });
      fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates: updates })
      })
        .then(function (r) { return r.json(); })
        .then(function () { window.location.reload(); })
        .catch(function () {});
    });
  }

  /* ── Build-a-Box bundle grouping ───────────────────────────────────
     Line items with a `_box_id` property share one bundle. Group them
     visually into one card per unique _box_id, disable their qty
     steppers and remove buttons, and show the box as a "Bundle (4 items)"
     summary row with a single remove-bundle action. */
  function groupBundleLines() {
    // Gather line-item rows in whichever cart surface is on the page.
    var rows = document.querySelectorAll(
      'cart-drawer .cart-item, #main-cart-items .cart-item, .tyk-cart-row'
    );
    if (!rows.length) return;

    // Each row has line item properties we can detect: look for the
    // "Bundle: N-Bag Box" text or a data-prop-box-id attribute.
    var groups = {};
    rows.forEach(function(row) {
      // Extract _box_id from dd/dt pairs, line-item property text, or data attr
      var boxId = extractBundleId(row);
      if (!boxId) return;
      if (!groups[boxId]) groups[boxId] = [];
      groups[boxId].push(row);
    });

    Object.keys(groups).forEach(function(boxId) {
      var lines = groups[boxId];
      if (lines.length < 2) return;  // single line — no grouping

      // Tag parent rows with a class so CSS can style them
      lines.forEach(function(row, i) {
        row.classList.add('tyk-bundle-line');
        row.classList.toggle('tyk-bundle-line--first', i === 0);
        row.classList.toggle('tyk-bundle-line--last', i === lines.length - 1);
        // Disable editing on bundle lines
        row.querySelectorAll('quantity-input button, .quantity__button, [data-tyk-cart-minus], [data-tyk-cart-plus]')
          .forEach(function(b) { b.disabled = true; b.setAttribute('aria-disabled', 'true'); });
        row.querySelectorAll('cart-remove-button, [data-tyk-cart-remove]').forEach(function(b) {
          b.setAttribute('data-bundle-member', '1');
        });
      });
    });
  }

  function extractBundleId(row) {
    // Look in the row's Liquid-rendered properties section.
    // Dawn renders hidden underscored props but also visible ones.
    // Strategy: search the row's textContent for the Bundle label, and use
    // a data-* or dd/dt pair Dawn emits. As a fallback, parse any
    // [data-cart-item-key] / [data-variant-id] by matching properties.
    var txt = row.textContent || '';
    // Some themes render property values inline; looking for our _box_id
    // value is unreliable because underscore-prefixed props are hidden.
    // Better: check for our public "Bundle" property text (we added
    // 'Bundle': 'N-Bag Box' alongside _box_id).
    var match = txt.match(/Bundle:\s*(\d+-Bag Box)/);
    if (!match) return null;

    // We need a stable ID to group rows. Since underscored props are
    // not in the DOM, use (product-id + Bundle label) as a best-effort
    // group key. If someone builds TWO boxes of the same size
    // simultaneously, they'll merge — acceptable trade-off.
    var pid = row.getAttribute('data-product-id')
           || row.getAttribute('data-variant-id')
           || row.getAttribute('data-cart-item-key')
           || '';
    return 'bundle-' + match[1].replace(/\s+/g, '-');
  }

  /* ── Progress bar ──────────────────────────────────────────────── */
  function setupProgressBar() { /* no-op, state read from DOM */ }

  function refreshProgressBar(cart) {
    var el = document.querySelector('[data-tyk-cart-progress]');
    if (!el) return;
    var threshold = parseInt(el.getAttribute('data-threshold-cents'), 10) || 0;
    if (threshold <= 0) return;
    var total = (cart && typeof cart.total_price === 'number') ? cart.total_price : 0;
    var remaining = Math.max(0, threshold - total);
    var pct = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));

    var fill = el.querySelector('[data-tyk-cart-progress-fill]');
    if (fill) fill.style.width = pct + '%';

    var msg = el.querySelector('[data-tyk-cart-progress-msg]');
    if (msg) {
      if (total >= threshold) {
        el.classList.add('is-unlocked');
        msg.textContent = el.getAttribute('data-msg-unlocked') || "You've unlocked FREE shipping!";
      } else {
        el.classList.remove('is-unlocked');
        var tpl = el.getAttribute('data-msg-in-progress') || 'Spend [amount] more for FREE shipping';
        msg.textContent = tpl.replace('[amount]', formatMoney(remaining));
      }
    }

    el.setAttribute('aria-valuenow', pct);
  }

  function formatMoney(cents) {
    // Simple USD fallback. Shopify stores currency format in
    // Shopify.currency but we can't assume it's loaded; this is
    // "close enough" for a UI hint — actual totals use money filter.
    var dollars = (cents / 100).toFixed(2);
    // Remove .00 suffix for cleaner display.
    if (dollars.endsWith('.00')) dollars = dollars.slice(0, -3);
    return '$' + dollars;
  }

  /* ── Promo code ────────────────────────────────────────────────── */
  function setupPromoInput() {
    var form = document.querySelector('[data-tyk-promo-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = form.querySelector('[data-tyk-promo-input]');
        var code = (input && input.value || '').trim().toUpperCase();
        if (!code) return;
        applyPromo(code);
      });
    }

    var removeBtn = document.querySelector('[data-tyk-promo-remove]');
    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        removePromo();
      });
    }

    // Also attach listeners to anything that gets injected later.
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-tyk-promo-remove]');
      if (t) {
        e.preventDefault();
        removePromo();
      }
    });
  }

  function applyPromo(code) {
    setPromoStatus('Applying…', '');
    // Save as cart attribute so it survives refreshes + is visible server-side.
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { tyk_promo: code } })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        setPromoStatus('Promo "' + code + '" queued — applied at checkout.', 'is-applied');
        showAppliedChip(code);
        refreshCheckoutUrl(cart);
      })
      .catch(function () {
        setPromoStatus('Could not save promo code. Try again?', 'is-error');
      });
  }

  function removePromo() {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { tyk_promo: '' } })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        hideAppliedChip();
        setPromoStatus('', '');
        refreshCheckoutUrl(cart);
      })
      .catch(function () {});
  }

  function refreshPromoApplied(cart) {
    var code = cart && cart.attributes && cart.attributes.tyk_promo;
    if (code) {
      showAppliedChip(code);
    } else {
      hideAppliedChip();
    }
  }

  function showAppliedChip(code) {
    var wrap = document.querySelector('[data-tyk-promo-wrap]');
    if (!wrap) return;
    var existing = wrap.querySelector('[data-tyk-promo-applied]');
    if (existing) {
      var codeEl = existing.querySelector('[data-tyk-promo-applied-code]');
      if (codeEl) codeEl.textContent = code.toUpperCase();
    } else {
      var chip = document.createElement('div');
      chip.className = 'tyk-cart-promo-applied';
      chip.setAttribute('data-tyk-promo-applied', '');
      chip.innerHTML =
        '<span>🎟 Promo queued:</span>' +
        '<strong data-tyk-promo-applied-code>' + escapeHtml(code.toUpperCase()) + '</strong>' +
        '<button type="button" aria-label="Remove promo code" data-tyk-promo-remove>✕</button>';
      wrap.insertBefore(chip, wrap.firstChild);
    }
    var form = wrap.querySelector('[data-tyk-promo-form]');
    if (form) {
      form.setAttribute('hidden', '');
      form.classList.add('is-hidden');
    }
  }

  function hideAppliedChip() {
    var chip = document.querySelector('[data-tyk-promo-applied]');
    if (chip && chip.parentNode) chip.parentNode.removeChild(chip);
    var form = document.querySelector('[data-tyk-promo-form]');
    if (form) {
      form.removeAttribute('hidden');
      form.classList.remove('is-hidden');
      var input = form.querySelector('[data-tyk-promo-input]');
      if (input) input.value = '';
    }
  }

  function setPromoStatus(msg, cls) {
    var el = document.querySelector('[data-tyk-promo-status]');
    if (!el) return;
    el.textContent = msg;
    el.className = 'tyk-cart-promo__status ' + (cls || '');
  }

  /* ── Rewrite checkout URL to apply the discount at checkout ─────── */
  function refreshCheckoutUrl(cart) {
    var code = cart && cart.attributes && cart.attributes.tyk_promo;
    var checkoutBtns = document.querySelectorAll('button[name="checkout"], a[href^="/checkout"], a[href*="/checkout"]');
    checkoutBtns.forEach(function (btn) {
      if (btn.tagName === 'BUTTON') {
        var form = btn.closest('form');
        if (form) {
          // For form-based checkout we rely on cart attribute + Shopify will pick it up
          // via a Liquid tag we inject if needed. But also stash on a data attr for JS hooks.
          if (code) form.setAttribute('data-tyk-promo', code);
          else form.removeAttribute('data-tyk-promo');
        }
      } else if (btn.tagName === 'A') {
        var url = btn.getAttribute('href') || '/checkout';
        // Strip any existing /discount/ prefix
        url = url.replace(/^\/discount\/[^/?]+\/?/, '/');
        if (code) {
          btn.setAttribute('href', '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(url));
        } else {
          btn.setAttribute('href', url);
        }
      }
    });

    // Intercept form submit to redirect to /discount/CODE/cart?... instead
    var forms = document.querySelectorAll('form[action*="/cart"]');
    forms.forEach(function (form) {
      if (form.__tykPromoBound) return;
      form.__tykPromoBound = true;
      form.addEventListener('submit', function (e) {
        var btn = e.submitter || form.querySelector('[name="checkout"]');
        if (!btn || btn.name !== 'checkout') return;
        var currentCode = form.getAttribute('data-tyk-promo');
        if (!currentCode) return;
        e.preventDefault();
        window.location.href = '/discount/' + encodeURIComponent(currentCode) + '?redirect=/checkout';
      });
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
})();
